/**
 * W-022 — Video upload panel. Stories: US-A04.
 * Phase-1 critical path (docs/06 §7): ingest of the ~100-hour library cannot
 * start until this works.
 *
 * AC1 progress with an estimated time remaining
 * AC2 resume after a connection drop — TUS, fingerprinted per file
 * AC3 continues while the operator navigates elsewhere in admin — the upload is
 *     owned by `lib/upload-manager`, not by this component
 * AC4 Uploading → Processing → Ready / Failed
 * AC5 retry encoding without re-uploading
 * AC6 in-panel preview of a ready video
 * AC7 an unsupported format is refused before a byte is sent
 */
import { CloudUploadOutlined, ReloadOutlined } from '@ant-design/icons'
import { useInvalidate } from '@refinedev/core'
import { Alert, Button, Descriptions, Progress, Space, Typography, Upload } from 'antd'
import type { UploadProps } from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'
import { StatusTag } from '@/components'
import { DiscardUploadDialog } from '@/components/dialogs'
import { recordAudit } from '@/lib/audit'
import { useAdminTranslate } from '@/providers/i18n-provider'
import { supabaseClient } from '@/lib/supabase'
import {
  ACCEPTED_VIDEO_EXTENSIONS,
  formatBytes,
  formatEta,
  getAdminPreviewUrl,
  retryEncoding,
  uploadManager,
  validateVideoFile,
} from '@/lib/upload-manager'
import type { UploadState } from '@/lib/upload-manager'
import type { VideoStatus } from '@/types'

export interface VideoUploadPanelProps {
  contentId: string
  title: string
  videoId: string | null
  videoStatus: VideoStatus
  /** Encoding failure reason, written by the Bunny webhook handler. */
  videoError?: string | null
  durationSec?: number | null
  disabled?: boolean
}

export const VideoUploadPanel = ({
  contentId,
  title,
  videoId,
  videoStatus,
  videoError,
  durationSec,
  disabled = false,
}: VideoUploadPanelProps) => {
  const translate = useAdminTranslate()
  const [state, setState] = useState<UploadState | undefined>(() =>
    uploadManager.getState(contentId),
  )
  const [status, setStatus] = useState<VideoStatus>(videoStatus)
  const [error, setError] = useState<string | null>(videoError ?? null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [discardOpen, setDiscardOpen] = useState(false)
  const invalidate = useInvalidate()
  const videoRef = useRef<HTMLVideoElement | null>(null)

  // AC3 — re-attach to an upload that started before this panel mounted.
  useEffect(() => uploadManager.subscribe(contentId, setState), [contentId])

  useEffect(() => {
    setStatus(videoStatus)
    setError(videoError ?? null)
  }, [videoStatus, videoError])

  /**
   * AC4 — the status chain is driven by the backend, not by the browser:
   * Bunny's webhook updates `content_items.video_status` (docs/03 §5.1) and the
   * realtime subscription pushes it here.
   */
  useEffect(() => {
    const channel = supabaseClient
      .channel(`content_item:${contentId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'content_items',
          filter: `id=eq.${contentId}`,
        },
        payload => {
          const next = payload.new as { video_status?: VideoStatus; video_error?: string | null }
          if (next.video_status) setStatus(next.video_status)
          setError(next.video_error ?? null)
          void invalidate({ resource: 'content_items', invalidates: ['detail'], id: contentId })
        },
      )
      .subscribe()

    return () => {
      void supabaseClient.removeChannel(channel)
    }
  }, [contentId, invalidate])

  const beforeUpload: UploadProps['beforeUpload'] = useCallback(
    (file: File) => {
      // AC7 — refuse before a byte moves.
      const invalid = validateVideoFile(file)
      if (invalid) {
        setError(invalid)
        return Upload.LIST_IGNORE
      }
      setError(null)
      void uploadManager.start({ contentId, file, title }).catch((caught: unknown) => {
        setError(
          caught instanceof Error
            ? caught.message
            : translate('admin.screens.videoPanel.uploadFailed'),
        )
      })
      // We drive the transfer ourselves; antd must not.
      return false
    },
    [contentId, title, translate],
  )

  const handleRetry = async () => {
    if (!videoId) return
    setError(null)
    try {
      const result = await retryEncoding(contentId, videoId)
      setStatus(result.status)
    } catch (caught) {
      setStatus('failed')
      setError(
        caught instanceof Error
          ? caught.message
          : translate('admin.screens.videoPanel.retryFailed'),
      )
    }
  }

  const handlePreview = async () => {
    try {
      setPreviewUrl(await getAdminPreviewUrl(contentId))
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : translate('admin.screens.videoPanel.previewUnavailable'),
      )
    }
  }

  const uploading = state?.phase === 'uploading' || state?.phase === 'creating'
  const effectiveStatus: VideoStatus = uploading
    ? 'uploading'
    : state?.phase === 'uploaded' && status === 'none'
      ? 'processing'
      : status

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space>
        <Typography.Text strong>{translate('admin.screens.videoPanel.title')}</Typography.Text>
        <StatusTag kind="video" value={effectiveStatus} reason={error} />
      </Space>

      {error ? (
        <Alert
          type="error"
          showIcon
          message={translate('admin.screens.videoPanel.failure')}
          description={error}
          action={
            status === 'failed' && videoId ? (
              // AC5 — the bytes are already at Bunny; only encoding is re-run.
              <Button size="small" icon={<ReloadOutlined />} onClick={() => void handleRetry()}>
                {translate('admin.screens.videoPanel.retryEncoding')}
              </Button>
            ) : null
          }
        />
      ) : null}

      {uploading || state?.phase === 'paused' ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Text type="secondary">{state?.fileName}</Typography.Text>
          <Progress percent={state?.percent ?? 0} status={uploading ? 'active' : 'normal'} />
          <Descriptions size="small" column={3}>
            <Descriptions.Item label={translate('admin.screens.videoPanel.sent')}>
              {formatBytes(state?.bytesSent ?? 0)} / {formatBytes(state?.bytesTotal ?? 0)}
            </Descriptions.Item>
            <Descriptions.Item label={translate('admin.screens.videoPanel.speed')}>
              {formatBytes(Math.round(state?.speed ?? 0))}/s
            </Descriptions.Item>
            <Descriptions.Item label={translate('admin.screens.videoPanel.remaining')}>
              {formatEta(state?.etaSeconds ?? null)}
            </Descriptions.Item>
          </Descriptions>
          <Space>
            {uploading ? (
              <Button size="small" onClick={() => uploadManager.pause(contentId)}>
                {translate('admin.screens.videoPanel.pause')}
              </Button>
            ) : (
              <Button size="small" onClick={() => uploadManager.resume(contentId)}>
                {translate('admin.screens.videoPanel.resume')}
              </Button>
            )}
            <Button size="small" danger onClick={() => setDiscardOpen(true)}>
              {translate('admin.screens.videoPanel.discard')}
            </Button>
          </Space>
          <Typography.Text type="secondary">
            {translate('admin.screens.videoPanel.backgroundHint')}
          </Typography.Text>
        </Space>
      ) : null}

      {effectiveStatus === 'processing' ? (
        <Alert
          type="info"
          showIcon
          message={translate('admin.screens.videoPanel.encodingTitle')}
          description={translate('admin.screens.videoPanel.encodingBody')}
        />
      ) : null}

      {effectiveStatus === 'ready' ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Descriptions size="small" column={2}>
            <Descriptions.Item label={translate('admin.screens.videoPanel.videoId')}>
              {videoId ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={translate('admin.columns.duration')}>
              {durationSec
                ? translate('admin.screens.videoPanel.durationMinutes', {
                    count: Math.round(durationSec / 60),
                  })
                : '—'}
            </Descriptions.Item>
          </Descriptions>
          {previewUrl ? (
            // AC6 — the preview URL is signed and short-lived (docs/03 §5.2).
            <video ref={videoRef} src={previewUrl} controls style={{ width: '100%' }} />
          ) : (
            <Button onClick={() => void handlePreview()}>
              {translate('admin.screens.videoPanel.preview')}
            </Button>
          )}
        </Space>
      ) : null}

      {!uploading && effectiveStatus !== 'processing' ? (
        <Upload.Dragger
          multiple={false}
          showUploadList={false}
          accept={ACCEPTED_VIDEO_EXTENSIONS.join(',')}
          beforeUpload={beforeUpload}
          disabled={disabled}
        >
          <p>
            <CloudUploadOutlined style={{ fontSize: 28 }} />
          </p>
          <p>
            {effectiveStatus === 'ready'
              ? translate('admin.screens.videoPanel.dropReplace')
              : translate('admin.screens.videoPanel.dropHere')}
          </p>
          <Typography.Text type="secondary">
            {translate('admin.screens.videoPanel.acceptedFormats', {
              formats: ACCEPTED_VIDEO_EXTENSIONS.join(', '),
            })}
          </Typography.Text>
        </Upload.Dragger>
      ) : null}

      {/* WD-3 */}
      <DiscardUploadDialog
        open={discardOpen}
        fileName={state?.fileName ?? ''}
        bytesSent={state?.bytesSent ?? 0}
        onCancel={() => setDiscardOpen(false)}
        onConfirm={async () => {
          await uploadManager.discard(contentId)
          await recordAudit({
            action: 'media.discard_upload',
            target: 'content_items',
            targetId: contentId,
            before: { file: state?.fileName, bytes_sent: state?.bytesSent },
          })
          setDiscardOpen(false)
        }}
      />
    </Space>
  )
}
